import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const caseTypes = ["General", "Contract", "Consumer", "Employment", "Family", "Property", "Criminal", "Tort"];
const statuses = ["Intake", "Active", "Pending", "Closed"];

export default function NewCasePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    case_type: "General",
    status: "Intake",
    jurisdiction: "Generic",
    case_number: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/cases", form);
      toast.success("Case opened.");
      navigate(`/app/cases/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create case");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="new-case-page">
      <PageHeader
        eyebrow="Nouveau Dossier"
        title="Open a new case"
        description="Start with what you know. You can always refine the details later."
      />
      <form onSubmit={onSubmit} className="max-w-2xl space-y-6" data-testid="new-case-form">
        <div>
          <Label className="label-eyebrow mb-2 block">Title</Label>
          <Input
            required
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="e.g., Dispute with landlord over security deposit"
            className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
            data-testid="case-title-input"
          />
        </div>
        <div>
          <Label className="label-eyebrow mb-2 block">What happened?</Label>
          <Textarea
            rows={5}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Describe the dispute in your own words. Dates, parties, amounts."
            className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
            data-testid="case-description-input"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className="label-eyebrow mb-2 block">Type</Label>
            <Select value={form.case_type} onValueChange={(v) => set("case_type", v)}>
              <SelectTrigger className="rounded-none border-[#E8E3F0] bg-white" data-testid="case-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {caseTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow mb-2 block">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="rounded-none border-[#E8E3F0] bg-white" data-testid="case-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className="label-eyebrow mb-2 block">Jurisdiction</Label>
            <Input
              value={form.jurisdiction}
              onChange={(e) => set("jurisdiction", e.target.value)}
              className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
              data-testid="case-jurisdiction-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow mb-2 block">Case number (optional)</Label>
            <Input
              value={form.case_number}
              onChange={(e) => set("case_number", e.target.value)}
              className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
              data-testid="case-number-input"
            />
          </div>
        </div>
        <div className="pt-4 flex gap-3">
          <Button type="submit" disabled={loading} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="case-submit-btn">
            {loading ? "Opening…" : "Open case"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-none border-[#E8E3F0]" data-testid="case-cancel-btn">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
